import type { ReactNode } from 'react';
import type { ElementType } from '@react-spring/web';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme/types';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { Box } from '../box/box';
import { Text } from '../text/text';

import * as styles from './tabs.css';

export type TabsProps = {
  children: ReactNode;
  className?: string;
  paddingX?: Spacing;
};

type ButtonOrLink =
  | {
      /**
       * the 'to' prop is used when we want the tab to act as a router link
       */
      id?: never;
      onClick?: never;
      to: string;
      NavLink: ElementType;
    }
  | {
      /**
       * the 'onClick' prop is used when we want the tab to act as a button
       */
      id: string;
      onClick: (id: string) => void;
      to?: never;
      NavLink?: never;
    };

const TEST_IDS = {
  CONTENT: 'tab-content',
};

export type TabsItemProps = {
  children: ReactNode;
  disabled?: boolean;
  selectedTab?: string;
} & ButtonOrLink;

export const TabsItem = ({
  children,
  disabled = false,
  id,
  onClick,
  selectedTab,
  to,
  NavLink,
}: TabsItemProps) => {
  const handleClick = id && onClick ? () => onClick(id) : undefined;

  return (
    <li>
      <Box
        Component={disabled ? 'span' : to ? NavLink : 'button'}
        className={classFilter([
          styles.reset,
          styles.item,
          selectedTab &&
            (selectedTab === id || (to && selectedTab.startsWith(to))) &&
            styles.active,
        ])}
        disabled={disabled}
        onClick={disabled ? undefined : handleClick}
        padding={{ bottom: 'baseNeg6', x: 'none' }}
        to={disabled ? undefined : to}
        type="button"
        // Disabling prefetch for now. I thought it would prefetch the data for immediately switching
        // but all that seems to happen is that it calls the loader on hover, then calls it again on click
        // this might be a subtle react router bug so we should revisit this later
        // prefetch="intent"
      >
        <Box
          className={classFilter([
            styles.content,
            !disabled && styles.contentEnabled,
          ])}
          data-testid={TEST_IDS.CONTENT}
          alignContent={{ y: 'center' }}
          rounded
        >
          <Text
            variant="captionMedium150"
            color={disabled ? 'neutral300' : undefined}
          >
            {children}
          </Text>
        </Box>
      </Box>
    </li>
  );
};

export const Tabs = ({ children, className, paddingX }: TabsProps) => (
  <nav>
    <Box
      Component="ul"
      stack="row"
      spacing="basePos2"
      className={classFilter([styles.container, className])}
      padding={paddingX ? { x: paddingX } : undefined}
    >
      {children}
    </Box>
  </nav>
);

Tabs.Item = TabsItem;
